import { FastField } from 'formik';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';

import { FormField, Loading, MultiPartForm } from '../../../components';
import { loadRecaptcha } from './recaptcha';
import {
  combineValidations,
  validateEmail,
  validateRequired,
  validatePhone
} from '../../../components/FormField/utils';
import { ErrorText } from '../../../components/FormField/components';
import {
  FormLayout,
  StyledForm,
  SuccessMessage,
  Title,
  Wrapper
} from './components';
import { form, initialValues } from './data';

const Field = props => <FastField component={FormField} {...props} />;

const siteKey = typeof process !== 'undefined' && process.env.RAZZLE_RECAPTCHA_SITE_KEY;

const Form = () => {
  const [loading, setLoading] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [getRecaptchaToken, setGetRecaptchaToken] = useState(null);

  const contentRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return;
    loadRecaptcha(siteKey)
      .then(fn => setGetRecaptchaToken(() => fn))
      .catch(() => setGetRecaptchaToken(null));
  }, []);

  const handleSubmit = async (values, actions) => {
    values[form.PHONE] = values[form.PHONE].replace(/[()-\s]/g, '');
    setLoading(true);
    setErrorMessage('');

    let recaptchaToken = '';
    if (getRecaptchaToken) {
      try {
        recaptchaToken = await getRecaptchaToken();
      } catch (e) {
        setErrorMessage('Security check failed. Please refresh and try again.');
        setLoading(false);
        return;
      }
    }

    const payload = { body: values };
    if (recaptchaToken) payload.recaptchaToken = recaptchaToken;

    axios
      .post('/franchising-inquiry', payload)
      .then(res => {
        setLoading(false);
        actions.setSubmitting(false);
        // Redirect to thank you page
        if (typeof window !== 'undefined') {
          const isLocal = window.location.pathname.startsWith('/franchising');
          const thankYouPath = isLocal 
            ? '/franchising/contact-form/thank-you'
            : '/contact-form/thank-you';
          window.location.href = thankYouPath;
        }
      })
      .catch(err => {
        const data = err.response?.data;
        if (data === 'DUPLICATES_DETECTED') {
          setErrorMessage('This data may have been submitted before.');
        } else if (data && data.code === 'RECAPTCHA_FAILED') {
          setErrorMessage(data.error || 'Verification failed. Please try again.');
        } else {
          setErrorMessage('There was a problem submitting your information. Please contact itsupport@salata.com');
        }
        setLoading(false);
      });
  };

  return (
    <Wrapper maxHeight={maxHeight} ref={contentRef} submitted={submitted}>
      <StyledForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitted={submitted}
      >
        <MultiPartForm.Page>
          <Title>Franchise Request Form</Title>

          <FormLayout>
            <Field
              columns={{ sm: 6 }}
              label="First Name"
              name={form.FIRST_NAME}
              type="text"
              validate={validateRequired}
            />
            <Field
              columns={{ sm: 6 }}
              label="Last Name"
              name={form.LAST_NAME}
              type="text"
              validate={validateRequired}
            />
            <Field
              label="Email Address"
              name={form.EMAIL}
              type="email"
              validate={combineValidations(validateRequired, validateEmail)}
            />
            <Field
              label="Phone Number"
              name={form.PHONE}
              type="tel"
              validate={combineValidations(validateRequired, validatePhone)}
              placeholder="(555) 555-5555"
            />
            <Field
              columns={{ sm: 6 }}
              label="Location of Interest (City)"
              name={form.LOCATION_CITY}
              type="text"
              validate={validateRequired}
            />
            <Field
              columns={{ sm: 6 }}
              label="Location of Interest (State)"
              name={form.LOCATION_STATE}
              type="state"
              placeholder="Select a state"
              validate={validateRequired}
            />
            <Field
              label="Notes"
              name={form.NOTES}
              placeholder="Enter any additional notes..."
              rows="4"
              type="textarea"
              required={false}
            />
          </FormLayout>
        </MultiPartForm.Page>
      </StyledForm>
      <Loading isLoading={loading} />
      <SuccessMessage
        bodyText="Your message has been sent. We'll be sure to reply as soon as we can."
        headline="got it"
        submitted={submitted}
      />
      <ErrorText>{errorMessage}</ErrorText>
    </Wrapper>
  );
};

export default Form;
